import React, { useState, useEffect } from "react";
import { useModal } from "../../context/ModalContext";

const API = process.env.REACT_APP_API_URL || "";

const SHIPPING_FIELDS = [
  { key: "shipping_base", label: "기본 배송비", unit: "원", desc: "국내 배송 시 기본 요금 (CJ대한통운)" },
  { key: "shipping_jeju_surcharge", label: "제주 추가 요금", unit: "원", desc: "제주 지역에 부과되는 추가 배송비" },
  { key: "shipping_island_surcharge", label: "도서지역 추가 요금", unit: "원", desc: "제주 외 도서지역에 부과되는 추가 배송비" },
  // { key: "shipping_intl", label: "해외 배송비 (정액)", unit: "원", desc: "해외 배송 시 적용되는 단일 정액 요금 (전 지역 동일, 기본 11,000원)" },
];

export default function AdminShippingSettings() {
  const { showAlert } = useModal();
  const [settings, setSettings] = useState({});
  const [initialSettings, setInitialSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [savingAll, setSavingAll] = useState(false);

  const ALL_KEYS = SHIPPING_FIELDS.map((f) => f.key);

  useEffect(() => {
    fetch(`${API}/api/app-settings`)
      .then((r) => r.json())
      .then((data) => {
        setSettings(data);
        setInitialSettings(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  async function saveField(key) {
    setSaving((p) => ({ ...p, [key]: true }));
    try {
      const res = await fetch(`${API}/api/app-settings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [key]: settings[key] }),
      });
      if (!res.ok) throw new Error("save failed");
      setInitialSettings((p) => ({ ...p, [key]: settings[key] }));
    } catch (e) {
      showAlert("저장 실패");
    }
    setSaving((p) => ({ ...p, [key]: false }));
  }

  function handleChange(key, value) {
    setSettings((p) => ({ ...p, [key]: value }));
  }

  function isDirty(key) {
    return (settings[key] || "") !== (initialSettings[key] || "");
  }

  const dirtyKeys = ALL_KEYS.filter((k) => isDirty(k));
  const dirtyCount = dirtyKeys.length;

  async function saveAll() {
    if (dirtyCount === 0) return;
    setSavingAll(true);
    try {
      const body = Object.fromEntries(dirtyKeys.map((k) => [k, settings[k]]));
      const res = await fetch(`${API}/api/app-settings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error("save failed");
      setInitialSettings((p) => ({ ...p, ...body }));
    } catch (e) {
      showAlert("일괄 저장 실패");
    }
    setSavingAll(false);
  }

  function revertAll() {
    setSettings((p) => {
      const next = { ...p };
      dirtyKeys.forEach((k) => { next[k] = initialSettings[k] || ""; });
      return next;
    });
  }

  if (loading) return <div className="admin-print-loading">로딩 중...</div>;

  return (
    <div className="admin-print-settings">
      <h1>배송비 설정</h1>
      <p className="admin-print-sub">국내·해외 배송비 및 지역별 추가 요금 관리</p>

      <section className="aps-section">
        <div className="aps-section-header">
          <h2>배송비</h2>
          <span>주문 페이지, 결제 페이지, 이용약관, 배송 안내 FAQ에 자동 반영됩니다.</span>
        </div>
        {SHIPPING_FIELDS.map((f) => (
          <div className="aps-row" key={f.key}>
            <div className="aps-label">
              <span className="aps-label-text">{f.label}</span>
              <span className="aps-label-key">{f.desc}</span>
            </div>
            <div className="aps-input-wrap">
              <input
                type="text"
                className={isDirty(f.key) ? "aps-input-dirty" : ""}
                value={settings[f.key] || ""}
                onChange={(e) => handleChange(f.key, e.target.value)}
              />
              {f.unit && <span className="aps-unit">{f.unit}</span>}
              <button
                className={isDirty(f.key) ? "aps-save-dirty" : ""}
                onClick={() => saveField(f.key)}
                disabled={saving[f.key]}
              >
                {saving[f.key] ? "..." : "저장"}
              </button>
            </div>
          </div>
        ))}
      </section>

      <div className={`aps-bulk-bar${dirtyCount > 0 ? " aps-bulk-dirty" : ""}`}>
        <div className="aps-bulk-info">
          {dirtyCount > 0 ? (
            <>변경된 항목 <strong>{dirtyCount}</strong>개</>
          ) : (
            "변경된 항목 없음"
          )}
        </div>
        <div className="aps-bulk-actions">
          <button onClick={revertAll} disabled={dirtyCount === 0 || savingAll}>
            되돌리기
          </button>
          <button
            className="aps-bulk-save"
            onClick={saveAll}
            disabled={dirtyCount === 0 || savingAll}
          >
            {savingAll ? "저장 중..." : `일괄 저장${dirtyCount > 0 ? ` (${dirtyCount})` : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}
